import { mount, unmount } from 'svelte';
import ResearchCanvas from './ResearchCanvas.svelte';
import { setIcons } from './icons.js';

/** Live instances by element, so navigating away can tear down exactly what was put up. */
const mounted = new Map();

/** Everything the canvas needs, read off the island element's data attributes. */
function buildProps(el) {
    const json = (value, fallback) => {
        try {
            return JSON.parse(value ?? '') ?? fallback;
        } catch (e) {
            return fallback;
        }
    };

    const status = document.querySelector('[data-research-status]');

    // The named icon vocabulary, PHP's copy — installed before any card renders so a card's
    // icon name resolves on first paint rather than flashing as text.
    setIcons(json(el.dataset.boardIcons, {}));

    return {
        target: el,
        props: {
            initial: json(el.dataset.board, { nodes: [], edges: [] }),
            initialVersion: el.dataset.boardVersion ?? '',
            saveUrl: el.dataset.boardSaveUrl ?? null,
            // Ops: the canvas sends what the user did, not what the board should now be.
            opsUrl: el.dataset.boardOpsUrl ?? null,
            initialSeq: Number(el.dataset.boardSeq ?? 0),
            // Who this person is on the presence channel — passed in rather than read off a
            // window global, which is invisible at the call site.
            me: json(el.dataset.boardMe, null),
            members: json(el.dataset.boardMembers, []),
            cardsUrl: el.dataset.boardCardsUrl ?? null,
            pinUrl: el.dataset.boardPinUrl ?? null,
            attachUrl: el.dataset.boardAttachUrl ?? null,
            contentTypes: json(el.dataset.boardContentTypes, []),
            csrf: el.dataset.boardCsrf ?? '',
            canEdit: el.dataset.boardCanEdit === '1',
            // Sandbox: the visitor may rearrange the board, but there is deliberately no
            // save address — every change lives and dies in their own browser. Used by the
            // public demo, where a shared board that anyone could edit would last a day.
            sandbox: el.dataset.boardSandbox === '1',
            // Chrome toggles. Absent attributes mean "on", so the app island is unchanged.
            showCardTools: el.dataset.boardCardTools !== '0',
            showControls: el.dataset.boardControls !== '0',
            showMinimap: el.dataset.boardMinimap !== '0',
            runbookFlow: el.dataset.boardRunbookFlow === '1',
            runbookFingerprint: el.dataset.boardRunbookFingerprint ?? '',
            stateKey: el.dataset.boardStateKey ?? '',
            // Live capture progress arrives on this board's private channel.
            boardId: el.dataset.boardId ?? '',
            onStatus: (text) => { if (status) status.textContent = text; },
        },
    };
}

/**
 * Mounts the research-board Svelte Flow canvas into any `[data-research-board]`
 * element (wire:ignore'd in Blade). Re-runs on Livewire navigations.
 */
function mountBoards() {
    // Islands whose element is gone from the document: torn down BEFORE anything mounts.
    // wire:navigate's prefetch swap can replace the body without `livewire:navigating` ever
    // firing for this module, and an instance whose DOM was ripped out keeps its effects and
    // its presence connection alive — a ghost on everyone else's canvas.
    for (const [el, instance] of mounted) {
        if (!el.isConnected) {
            try {
                unmount(instance);
            } catch (e) { /* already gone */ }
            mounted.delete(el);
        }
    }

    document.querySelectorAll('[data-research-board]').forEach((el) => {
        // Mid-swap, elements exist that are not yet (or no longer) part of the document.
        // Mounting into one measures a canvas with no layout, which cascades forever.
        if (!el.isConnected || el.dataset.mounted || el.childElementCount > 0) {
            return;
        }
        el.dataset.mounted = '1';

        try {
            mounted.set(el, mount(ResearchCanvas, buildProps(el)));
        } catch (e) {
            // A crashed island must SAY so. Four rounds of effect_update_depth_exceeded
            // shipped as a silently dead canvas that also took presence and live updates
            // down with it — the person just saw nothing working, with no reason why.
            el.innerHTML = '<div style="padding:2rem;text-align:center;font:13px/1.5 system-ui;color:#b45309">'
                + 'The canvas crashed while loading. Reload the page; if it happens again, '
                + 'copy the browser console output and report it.</div>';
            delete el.dataset.mounted;
            throw e;
        }
    });
}

/**
 * Take the canvas down when the page goes away.
 *
 * Livewire navigation swaps the body without ever unmounting this island, so Svelte's cleanup
 * never ran: the presence connection stayed open and the person who navigated away remained
 * on everyone else's workspace as a ghost — still listed, still marking whatever card they had
 * selected — until those people reloaded.
 *
 * `pagehide` rather than `beforeunload`: it fires for a closed tab AND for a phone putting the
 * page in the background, where beforeunload does not.
 */
function unmountBoards() {
    for (const [el, instance] of mounted) {
        try {
            unmount(instance);
        } catch (e) { /* already gone */ }
        delete el.dataset.mounted;
        mounted.delete(el);
    }
}

/**
 * Never mount inside the current task.
 *
 * This module used to call mountBoards() bare at import time. On a wire:navigate after a
 * deploy, the prefetched page references NEW asset files, and Livewire executes them in the
 * middle of swapCurrentPageWithNewHtml — so the canvas mounted synchronously into a
 * half-swapped document. A zero-delay timeout runs after the swap's task completes, when the
 * document is whole.
 */
let mountTimer = null;

function scheduleMount() {
    clearTimeout(mountTimer);
    mountTimer = setTimeout(mountBoards, 0);
}

scheduleMount();
document.addEventListener('DOMContentLoaded', scheduleMount);
document.addEventListener('livewire:navigated', scheduleMount);
document.addEventListener('livewire:navigating', unmountBoards);
window.addEventListener('research-canvas-ready', scheduleMount);
window.addEventListener('pagehide', unmountBoards);
